import { expenseGroupOf, visibleCategory } from './finance';
import type { CostNature, ExpenseCategory, ExpenseEntry, TenantSettings } from './types';

/**
 * FIJO O VARIABLE, POR CATEGORÍA (regla 7 de Gastos, decidido con Alex el
 * 11 oct 2026): no se pregunta en cada gasto, lo decide la categoría, y se
 * puede cambiar por categoría en Configuración.
 *
 * Valores por defecto de la nota azul «CATEGORÍAS». Los usa el punto de
 * equilibrio de Análisis y el de los KPIs, para que los dos cuadren con el
 * Estado de resultados.
 *
 * Una sola fuente: `TenantSettings.expenseCategoryNature` (Configuración ›
 * Gastos y compras guarda solo las categorías que el usuario cambió). Las
 * claves viejas se leen como su equivalente (`visibleCategory`).
 */
export const NATURALEZA_POR_DEFECTO: Record<ExpenseCategory, CostNature> = {
  // Materia prima (variable)
  compras_materia_prima: 'variable',
  bebidas: 'variable',
  empaque: 'variable',
  otros_insumos: 'variable',
  // Personal (fijo)
  sueldos_salarios: 'fijo',
  cuotas_ss: 'fijo',
  otros_personal: 'fijo',
  uniformidad: 'fijo',
  // Gastos generales
  alquiler: 'fijo',
  suministros: 'variable',
  suministros_agua: 'variable',
  suministros_electricidad: 'variable',
  suministros_gas: 'variable',
  telefonia: 'fijo',
  reparaciones: 'variable',
  limpieza: 'variable',
  publicidad_rrss: 'fijo',
  comisiones_bancarias: 'variable',
  honorarios: 'fijo',
  comunicacion_web: 'fijo',
  seguros: 'fijo',
  papeleria: 'variable',
  otros_gastos: 'variable',
  servicios_varios: 'variable',
  // Debajo del EBITDA: el desgaste y los intereses se pagan abras o no; el
  // impuesto sobre la ganancia sube y baja con ella.
  amortizaciones: 'fijo',
  intereses: 'fijo',
  impuesto_ganancias: 'variable',
};

/** Ajuste por categoría (lo pone Configuración › Gastos y compras). */
export type AjusteNaturalezas = Pick<TenantSettings, 'expenseCategoryNature'>;

/** Clave de una subcategoría propia en `expenseCategoryNature`: «generales:Valet parking». */
export function claveDeSubcategoria(categoria: ExpenseCategory | string, subgroup: string): string | undefined {
  const grupo = expenseGroupOf(visibleCategory(categoria as ExpenseCategory));
  return grupo ? `${grupo}:${subgroup}` : undefined;
}

/**
 * ¿Es fijo o variable? El ajuste de la cuenta manda: primero el de la
 * subcategoría propia (si se puso), luego el de la categoría; si no, el valor
 * por defecto.
 */
export function naturalezaDe(categoria: ExpenseCategory | string, settings?: AjusteNaturalezas | null, subgroup?: string): CostNature {
  const cat = categoria as ExpenseCategory;
  const visible = visibleCategory(cat);
  const ajustes = settings?.expenseCategoryNature;
  const clave = subgroup ? claveDeSubcategoria(cat, subgroup) : undefined;
  return (
    (clave ? ajustes?.[clave] : undefined) ??
    ajustes?.[visible] ??
    ajustes?.[cat] ??
    NATURALEZA_POR_DEFECTO[visible] ??
    NATURALEZA_POR_DEFECTO[cat] ??
    'variable'
  );
}

/**
 * Los gastos con el fijo/variable de su categoría. El motor del resultado
 * (punto de equilibrio, «Fijos y variables» de Análisis) lee `nature`; así lo
 * lee de la categoría sin tocar el motor. El dato guardado en cada gasto se
 * conserva tal cual (compatibilidad), pero ya no manda.
 */
export function conNaturalezaDeCategoria<T extends Pick<ExpenseEntry, 'category' | 'nature'> & { subgroup?: string }>(
  expenses: T[],
  settings?: AjusteNaturalezas | null,
): T[] {
  return expenses.map((e) => {
    const nature = naturalezaDe(e.category, settings, e.subgroup);
    return e.nature === nature ? e : { ...e, nature };
  });
}
